import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import EmployeePayrollSheet from '../components/payroll/EmployeePayrollSheet';
import BulletinsTab from '../components/payroll/BulletinsTab';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Search,
  Filter,
  Check,
  Download,
  Clock,
  Moon,
  Sun,
  Euro,
  Calculator,
  ChevronLeft,
  ChevronRight,
  Copy,
  RefreshCw,
  FileSpreadsheet,
  AlertTriangle,
  Upload,
  Server,
  FileText,
  Trash2,
  Edit,
  Calendar,
  Wallet,
  Coffee,
  Lock,
  FileDown,
  Archive,
  X,
  Gavel,
  Scale,
  Info,
  CheckCircle2,
  XCircle,
  ExternalLink
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Payroll = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('preparation');
  const [recaps, setRecaps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('nom');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingCell, setEditingCell] = useState(null); // { employeeId, field }
  const [editingValue, setEditingValue] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Payroll Provider state (multi-provider support)
  const [syncingPayroll, setSyncingPayroll] = useState(false);
  const [payrollConfig, setPayrollConfig] = useState(null);
  const [payrollProviders, setPayrollProviders] = useState({});

  // Lock status
  const [isLocked, setIsLocked] = useState(false);
  const [dateLimite, setDateLimite] = useState(null);
  const [lockMessage, setLockMessage] = useState(null);

  // Calculator state
  const [calcEmployee, setCalcEmployee] = useState('');
  const [calcHours, setCalcHours] = useState('');
  const [calcType, setCalcType] = useState('normal');
  const [calcResult, setCalcResult] = useState(null);

  // Pointage dialog
  const [isPointageDialogOpen, setIsPointageDialogOpen] = useState(false);
  const [pointageData, setPointageData] = useState({
    employee_id: '',
    date: '',
    heures_travaillees: 0,
    heures_sup_25: 0,
    heures_sup_50: 0,
    heures_nuit: 0,
    heures_dimanche: 0,
    commentaire: ''
  });

  // Prime dialog
  const [isPrimeDialogOpen, setIsPrimeDialogOpen] = useState(false);
  const [primeData, setPrimeData] = useState({
    employee_id: '',
    code_rubrique: '',
    libelle: '',
    quantite: 1,
    montant: 0,
    type_element: 'prime'
  });

  // Acompte dialog
  const [isAcompteDialogOpen, setIsAcompteDialogOpen] = useState(false);
  const [acompteData, setAcompteData] = useState({
    employee_id: '',
    montant: 0,
    date_versement: '',
    motif: ''
  });

  // Jour férié travaillé dialog
  const [isJourFerieDialogOpen, setIsJourFerieDialogOpen] = useState(false);
  const [jourFerieData, setJourFerieData] = useState({
    employee_id: '',
    date: '',
    heures_travaillees: 0,
    taux_majoration: null
  });

  // Edit element dialog
  const [isEditElementDialogOpen, setIsEditElementDialogOpen] = useState(false);
  const [editElementData, setEditElementData] = useState(null);
  const [customPeriod, setCustomPeriod] = useState(null);

  // Saisies sur salaire
  const emptySaisie = {
    employee_id: '', creancier: '', reference: '', montant_total: '', mode: 'mensualite',
    mensualite: '', quotite_pct: '', date_debut: '', date_fin: '', commentaire: ''
  };
  const [saisies, setSaisies] = useState([]);
  const [isSaisieDialogOpen, setIsSaisieDialogOpen] = useState(false);
  const [saisieData, setSaisieData] = useState(emptySaisie);
  const [editingSaisieId, setEditingSaisieId] = useState(null);
  const [isSaisiesListOpen, setIsSaisiesListOpen] = useState(false);
  const [saisiesListEmployee, setSaisiesListEmployee] = useState('all');

  // Dialog d'édition complète d'une ligne
  const [isEditRecapOpen, setIsEditRecapOpen] = useState(false);
  const [editRecapEmployeeId, setEditRecapEmployeeId] = useState(null);
  const openEmployeeSheet = (employeeId) => { setEditRecapEmployeeId(employeeId); setIsEditRecapOpen(true); };

  useEffect(() => {
    fetchData();
    checkPayrollConfig();
    fetchCustomPeriod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const fetchCustomPeriod = async () => {
    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const res = await axios.get(`${API_URL}/admin/periodes-paie`, { params: { annee: year } });
      const found = res.data.find(p => p.mois === month);
      setCustomPeriod(found || null);
    } catch {
      setCustomPeriod(null);
    }
  };

  const checkPayrollConfig = async () => {
    try {
      const [configRes, providersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/payroll-config`),
        axios.get(`${API_URL}/payroll/providers`)
      ]);
      setPayrollConfig(configRes.data);
      setPayrollProviders(providersRes.data || {});
    } catch (error) {
      setPayrollConfig(null);
    }
  };

  const fetchData = async () => {
    try {
      const [recapsRes, empRes, saisiesRes] = await Promise.all([
        axios.get(`${API_URL}/paie/recap/${selectedMonth}`),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/paie/saisies`, { params: { periode: selectedMonth } }).catch(() => ({ data: [] }))
      ]);
      setSaisies(Array.isArray(saisiesRes.data) ? saisiesRes.data : []);

      // Handle new response format with lock status
      const recapsData = recapsRes.data;
      if (recapsData && recapsData.recaps !== undefined) {
        // New format with lock info
        setRecaps(recapsData.recaps || []);
        setIsLocked(recapsData.is_locked || false);
        setDateLimite(recapsData.date_limite || null);
        setLockMessage(recapsData.message_verrouillage || null);
      } else {
        // Old format (array directly)
        setRecaps(recapsData || []);
        setIsLocked(false);
        setDateLimite(null);
        setLockMessage(null);
      }

      setEmployees(empRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getProviderName = () => {
    if (!payrollConfig?.payroll_provider) return null;
    return payrollConfig.provider_name || payrollProviders[payrollConfig.payroll_provider]?.name || payrollConfig.payroll_provider;
  };

  // Capacités du logiciel de paie (voir /admin/payroll-config -> PayrollGateway.describe())
  const payrollCapabilities = payrollConfig?.capabilities || [];
  const hasCapability = (cap) => payrollCapabilities.includes(cap);
  const canPushToProvider = !!payrollConfig?.payroll_provider && hasCapability('variables.push');
  const exportFileIsPrimary = !canPushToProvider || hasCapability('variables.export_file');
  const capabilityLabels = payrollConfig?.capability_labels || {};
  const allCapabilityLabels = payrollConfig?.all_capabilities || {
    'employees.sync': 'Synchroniser les salariés',
    'variables.push': 'Envoyer les éléments variables',
    'variables.export_file': 'Exporter les éléments variables dans un fichier',
    'absences.push': 'Transmettre les absences',
    'payslips.list': 'Lister les bulletins',
    'payslips.pdf': 'Récupérer le PDF des bulletins',
    'payslips.amounts': 'Récupérer brut / net des bulletins',
    'payslips.cotisations_detail': 'Récupérer le détail des cotisations (patronales / salariales)',
    'payslips.upload': 'Dépôt manuel de bulletins PDF',
    'leave_balances.read': 'Lire les soldes de congés',
    'diagnostic': 'Diagnostic des réponses brutes du logiciel',
  };

  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [syncWarningData, setSyncWarningData] = useState(null);
  const [syncPreview, setSyncPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [exportingFile, setExportingFile] = useState(false);

  // Ouvre l'aperçu de l'envoi (période complète) avant toute transmission
  const handleSyncToPayroll = async () => {
    if (!payrollConfig?.payroll_provider) {
      toast.error('Aucun logiciel de paie configuré. Allez dans Administration > Intégrations pour configurer.');
      return;
    }
    if (!canPushToProvider) {
      toast.error(`${getProviderName()} ne prend pas en charge l'envoi des éléments variables : utilisez « Exporter le fichier paie ».`);
      return;
    }
    setLoadingPreview(true);
    setSyncPreview(null);
    setSyncWarningData(null);
    setShowSyncConfirm(true);
    try {
      const [previewRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/payroll/export-preparation/${selectedMonth}/apercu`),
        axios.get(`${API_URL}/payroll/sync-status/${selectedMonth}`).catch(() => ({ data: null })),
      ]);
      setSyncPreview(previewRes.data);
      if (statusRes.data?.already_synced) setSyncWarningData(statusRes.data);
    } catch (error) {
      setShowSyncConfirm(false);
      toast.error(error.response?.data?.detail || 'Impossible de préparer l\'aperçu de l\'envoi');
    } finally {
      setLoadingPreview(false);
    }
  };

  const executeSyncToPayroll = async () => {
    setShowSyncConfirm(false);
    setSyncWarningData(null);
    setSyncingPayroll(true);
    try {
      const response = await axios.post(`${API_URL}/payroll/export-preparation/${selectedMonth}`);
      const data = response.data;
      if (data.success) {
        toast.success(`Envoi vers ${data.provider} réussi : ${data.acceptes ?? data.elements_exported} élément(s) accepté(s) pour ${data.employees_exported} salarié(s)`);
      } else if (data.elements_exported === 0) {
        toast.info(data.message || 'Aucun élément variable à envoyer');
      } else {
        toast.error(`${data.message || 'Envoi partiel'} — ${(data.rejetes || []).length} élément(s) rejeté(s) par ${data.provider}`);
      }
      if ((data.a_corriger || []).length > 0) {
        toast.warning(`${data.a_corriger.length} élément(s) non transmis (rubrique non mappée ou unité non prise en charge). Voir Administration > Export paie.`);
      }
      if (data.absences && data.absences.success === false) {
        toast.warning(data.absences.message || 'Absences non transmises');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la synchronisation');
    } finally {
      setSyncingPayroll(false);
      setSyncPreview(null);
    }
  };

  // Fichier des éléments variables (CSV / XLSX) à importer dans le logiciel de paie
  const handleExportFile = async (format = 'csv') => {
    setExportingFile(true);
    try {
      const response = await axios.get(`${API_URL}/payroll/export-file/${selectedMonth}`, {
        params: { format },
        responseType: 'blob',
      });
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `elements_variables_${selectedMonth}.${format}`;
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      const count = response.headers['x-elements-count'];
      const excluded = parseInt(response.headers['x-excluded-count'] || '0', 10);
      const nonMappes = parseInt(response.headers['x-non-mappes-count'] || '0', 10);
      toast.success(`Fichier paie ${format.toUpperCase()} généré${count !== undefined ? ` (${count} élément(s))` : ''}`);
      if (excluded > 0 || nonMappes > 0) {
        toast.warning(`${nonMappes} rubrique(s) sans code et ${excluded} élément(s) exclus pour unité non prise en charge. Voir Administration > Export paie.`);
      }
    } catch (error) {
      let detail = 'Erreur lors de la génération du fichier paie';
      if (error.response?.data instanceof Blob) {
        try { detail = JSON.parse(await error.response.data.text()).detail || detail; } catch { /* ignore */ }
      }
      toast.error(detail);
    } finally {
      setExportingFile(false);
    }
  };


  const handleCalculate = async () => {
    if (!calcEmployee || !calcHours) {
      toast.error('Sélectionnez un employé et entrez le nombre d\'heures');
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/paie/calculator`, {
        params: {
          employee_id: calcEmployee,
          nb_heures: parseFloat(calcHours),
          type_heures: calcType
        }
      });
      setCalcResult(response.data);
    } catch (error) {
      toast.error('Erreur de calcul');
    }
  };

  const handleAddPointage = async (e) => {
    e.preventDefault();
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    try {
      await axios.post(`${API_URL}/pointages`, pointageData);
      toast.success('Pointage enregistré');
      setIsPointageDialogOpen(false);
      setPointageData({
        employee_id: '',
        date: '',
        heures_travaillees: 0,
        heures_sup_25: 0,
        heures_sup_50: 0,
        heures_nuit: 0,
        heures_dimanche: 0,
        commentaire: ''
      });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleAddPrime = async (e) => {
    e.preventDefault();
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    try {
      await axios.post(`${API_URL}/paie/elements`, {
        ...primeData,
        periode: selectedMonth
      });
      toast.success('Élément de paie ajouté');
      setIsPrimeDialogOpen(false);
      setPrimeData({
        employee_id: '',
        code_rubrique: '',
        libelle: '',
        quantite: 1,
        montant: 0,
        type_element: 'prime'
      });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erreur lors de l\'ajout');
    }
  };

  // Ajouter un acompte
  const handleAddAcompte = async (e) => {
    e.preventDefault();
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    try {
      await axios.post(`${API_URL}/paie/acomptes`, {
        ...acompteData,
        periode: selectedMonth
      });
      toast.success('Acompte ajouté');
      setIsAcompteDialogOpen(false);
      setAcompteData({ employee_id: '', montant: 0, date_versement: '', motif: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  // Supprimer un acompte
  const handleDeleteAcompte = async (acompteId) => {
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    if (!window.confirm('Supprimer cet acompte ?')) return;
    try {
      await axios.delete(`${API_URL}/paie/acomptes/${acompteId}`);
      toast.success('Acompte supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // ---- Saisies sur salaire
  const openCreateSaisie = (employeeId = '') => {
    setEditingSaisieId(null);
    setSaisieData({ ...emptySaisie, employee_id: employeeId, date_debut: `${selectedMonth}-01` });
    setIsSaisieDialogOpen(true);
  };

  const openEditSaisie = (saisie) => {
    setEditingSaisieId(saisie.id);
    setSaisieData({
      employee_id: saisie.employee_id,
      creancier: saisie.creancier || '',
      reference: saisie.reference || '',
      montant_total: saisie.montant_total ?? '',
      mode: saisie.mode || 'mensualite',
      mensualite: saisie.mensualite ?? '',
      quotite_pct: saisie.quotite_pct ?? '',
      date_debut: saisie.date_debut || '',
      date_fin: saisie.date_fin || '',
      commentaire: saisie.commentaire || ''
    });
    setIsSaisieDialogOpen(true);
  };

  const openSaisiesList = (employeeId = 'all') => {
    setSaisiesListEmployee(employeeId || 'all');
    setIsSaisiesListOpen(true);
  };

  const handleSaveSaisie = async (e) => {
    e.preventDefault();
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    const payload = {
      creancier: saisieData.creancier,
      reference: saisieData.reference || null,
      montant_total: parseFloat(saisieData.montant_total) || 0,
      mode: saisieData.mode,
      mensualite: saisieData.mode === 'mensualite' ? (parseFloat(saisieData.mensualite) || 0) : null,
      quotite_pct: saisieData.mode === 'quotite' ? (parseFloat(saisieData.quotite_pct) || 0) : null,
      date_fin: saisieData.date_fin || null,
      commentaire: saisieData.commentaire || null
    };
    try {
      if (editingSaisieId) {
        await axios.put(`${API_URL}/paie/saisies/${editingSaisieId}`, payload, { params: { periode: selectedMonth } });
        toast.success('Saisie sur salaire modifiée');
      } else {
        await axios.post(`${API_URL}/paie/saisies`, {
          ...payload,
          employee_id: saisieData.employee_id,
          date_debut: saisieData.date_debut
        }, { params: { periode: selectedMonth } });
        toast.success('Saisie sur salaire créée');
      }
      setIsSaisieDialogOpen(false);
      setEditingSaisieId(null);
      setSaisieData(emptySaisie);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleToggleSuspendSaisie = async (saisie) => {
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    const newStatut = saisie.statut === 'suspendue' ? 'active' : 'suspendue';
    try {
      await axios.put(`${API_URL}/paie/saisies/${saisie.id}`, { statut: newStatut }, { params: { periode: selectedMonth } });
      toast.success(newStatut === 'suspendue' ? 'Saisie suspendue' : 'Saisie réactivée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteSaisie = async (saisieId) => {
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    if (!window.confirm('Supprimer cette saisie sur salaire ?')) return;
    try {
      await axios.delete(`${API_URL}/paie/saisies/${saisieId}`, { params: { periode: selectedMonth } });
      toast.success('Saisie supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getSaisieStatusBadge = (statut) => {
    switch (statut) {
      case 'soldee':
        return <Badge className="bg-emerald-100 text-emerald-800">Soldée</Badge>;
      case 'suspendue':
        return <Badge className="bg-amber-100 text-amber-800">Suspendue</Badge>;
      default:
        return <Badge className="bg-rose-100 text-rose-800">Active</Badge>;
    }
  };

  // Ligne validée : la saisie inline est bloquée tant que la paie n'est pas dévalidée
  const isRowValidated = (employeeId) => recaps.some(r => r.employee_id === employeeId && r.statut === 'valide');
  const rowGuard = (employeeId) => {
    if (isLocked) {
      toast.error('Période verrouillée');
      return false;
    }
    if (isRowValidated(employeeId)) {
      toast.error('Paie validée : dévalidez d\'abord pour modifier');
      return false;
    }
    return true;
  };

  // Déclarer un jour férié travaillé
  const handleAddJourFerieTravaille = async (e) => {
    e.preventDefault();
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    try {
      await axios.post(`${API_URL}/paie/jours-feries-travailles`, jourFerieData);
      toast.success('Jour férié travaillé enregistré');
      setIsJourFerieDialogOpen(false);
      setJourFerieData({ employee_id: '', date: '', heures_travaillees: 0, taux_majoration: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Modifier un élément de paie
  const handleUpdateElement = async (e) => {
    e.preventDefault();
    if (!editElementData) return;
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    try {
      await axios.put(`${API_URL}/paie/elements/${editElementData.id}`, {
        montant: editElementData.montant,
        quantite: editElementData.quantite,
        libelle: editElementData.libelle
      });
      toast.success('Élément modifié');
      setIsEditElementDialogOpen(false);
      setEditElementData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  // Supprimer un élément de paie
  const handleDeleteElement = async (elementId) => {
    if (isLocked) {
      toast.error('Cette période est verrouillée. Modifications impossibles.');
      return;
    }
    if (!window.confirm('Supprimer cet élément ?')) return;
    try {
      await axios.delete(`${API_URL}/paie/elements/${elementId}`);
      toast.success('Élément supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleValidate = async (employeeId) => {
    try {
      await axios.post(`${API_URL}/paie/validate/${selectedMonth}?employee_id=${employeeId}`);
      toast.success('Paie validée avec archivage des données');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleInvalidate = async (employeeId) => {
    if (!window.confirm('Invalider cette paie ? Le snapshot archivé sera supprimé et les données pourront être modifiées.')) return;
    try {
      await axios.post(`${API_URL}/paie/invalidate/${selectedMonth}?employee_id=${employeeId}`);
      toast.success('Validation annulée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'invalidation');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/paie/export/${selectedMonth}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export_paie_${selectedMonth}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success('Export CSV réussi');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Export PDF pour archivage
  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/paie/export-pdf/${selectedMonth}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `preparation_paie_${selectedMonth}.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success('PDF généré avec succès');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Endpoint PDF non disponible');
      } else {
        toast.error('Erreur lors de la génération du PDF');
      }
    }
  };

  const changeMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    let newYear = year;
    let newMonth = month + direction;
    
    if (newMonth > 12) { newMonth = 1; newYear++; }
    else if (newMonth < 1) { newMonth = 12; newYear--; }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const getMonthLabel = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'valide':
        return <Badge className="bg-emerald-100 text-emerald-800">Validé</Badge>;
      case 'exporte':
        return <Badge className="bg-blue-100 text-blue-800">Exporté</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-800">Brouillon</Badge>;
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Parse employee_name "Prénom Nom" into separate fields for sort
  const parseRecap = (r) => {
    const parts = (r.employee_name || '').split(' ');
    return {
      ...r,
      _prenom: parts[0] || '',
      _nom: parts.length > 1 ? parts.slice(1).join(' ') : parts[0] || '',
      _matricule: r.matricule_paie || r.matricule || ''
    };
  };

  const filteredRecaps = recaps.map(parseRecap).filter(r => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = r._nom.toLowerCase().includes(searchLower)
      || r._prenom.toLowerCase().includes(searchLower)
      || r._matricule.toLowerCase().includes(searchLower);
    const matchesFilter = filterStatus === 'all' || r.statut === filterStatus;
    return matchesSearch && matchesFilter;
  }).sort((a, b) => {
    let valA, valB;
    if (sortField === 'nom') {
      valA = a._nom.toLowerCase();
      valB = b._nom.toLowerCase();
    } else if (sortField === 'prenom') {
      valA = a._prenom.toLowerCase();
      valB = b._prenom.toLowerCase();
    } else if (sortField === 'matricule') {
      valA = a._matricule;
      valB = b._matricule;
    } else {
      valA = a[sortField] || 0;
      valB = b[sortField] || 0;
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleInlineEdit = async (employeeId, field, value) => {
    if (!rowGuard(employeeId)) {
      setEditingCell(null);
      return;
    }
    const numValue = parseFloat(value) || 0;
    try {
      await axios.put(`${API_URL}/paie/recap/${selectedMonth}/${employeeId}`, {
        [field]: numValue
      });
      setEditingCell(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de mise à jour');
    }
  };

  const handleInlineNotes = async (employeeId, notes) => {
    if (!rowGuard(employeeId)) {
      setEditingCell(null);
      return;
    }
    try {
      await axios.put(`${API_URL}/paie/recap/${selectedMonth}/${employeeId}`, {
        commentaire: notes
      });
      setEditingCell(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de mise à jour');
    }
  };

  const handleInlinePaniers = async (employeeId, nbJours) => {
    if (!rowGuard(employeeId)) { setEditingCell(null); return; }
    const numValue = parseInt(nbJours) || 0;
    try {
      await axios.put(`${API_URL}/paie/recap/${selectedMonth}/${employeeId}/paniers`, {
        nb_jours: numValue
      });
      setEditingCell(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de mise à jour paniers');
    }
  };

  const SortHeader = ({ field, label, className = '' }) => (
    <TableHead
      className={`table-header cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700 ${className}`}
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (
          <span className="text-[10px]">{sortDirection === 'asc' ? '▲' : '▼'}</span>
        )}
      </span>
    </TableHead>
  );

  const EditableCell = ({ employeeId, field, value, suffix = '', className = '' }) => {
    const isEditing = editingCell?.employeeId === employeeId && editingCell?.field === field;
    const validated = isRowValidated(employeeId);
    const rowLocked = isLocked || validated;
    if (isEditing) {
      return (
        <TableCell className={`table-cell ${className}`}>
          <Input
            type="number"
            step="0.5"
            className="h-7 w-20 text-sm text-center"
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={() => handleInlineEdit(employeeId, field, editingValue)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleInlineEdit(employeeId, field, editingValue);
              if (e.key === 'Escape') setEditingCell(null);
            }}
            autoFocus
          />
        </TableCell>
      );
    }
    return (
      <TableCell
        className={`table-cell ${className} ${!rowLocked ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800' : ''}`}
        onDoubleClick={() => {
          if (!rowLocked) {
            setEditingCell({ employeeId, field });
            setEditingValue(String(value || 0));
          }
        }}
        title={!rowLocked ? 'Double-cliquez pour modifier' : (validated ? 'Paie validée : dévalidez pour modifier' : '')}
      >
        <span className={value > 0 ? 'font-semibold text-amber-600' : 'text-slate-400'}>
          {value}{suffix}
        </span>
      </TableCell>
    );
  };

  const totals = {
    heures_sup: recaps.reduce((sum, r) => sum + r.total_heures_sup, 0),
    heures_nuit: recaps.reduce((sum, r) => sum + r.heures_nuit, 0),
    primes: recaps.reduce((sum, r) => sum + r.total_primes, 0),
    deductions: recaps.reduce((sum, r) => sum + r.total_deductions, 0)
  };

  const [primeTypes, setPrimeTypes] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/admin/prime-types`).then(res => setPrimeTypes(res.data || [])).catch(() => setPrimeTypes([
      { code: 'PRIME_EXCEPT', label: 'Prime exceptionnelle' },
      { code: 'IND_KM', label: 'Indemnité kilométrique' },
    ]));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  const sheetIndex = filteredRecaps.findIndex((r) => r.employee_id === editRecapEmployeeId);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Préparation Paie</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Éléments variables mensuels
            </p>
            {payrollConfig?.payroll_provider ? (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="inline-flex" data-testid="payroll-capabilities-badge">
                      <Badge variant={payrollConfig.configured === false ? 'outline' : 'secondary'} className={`text-xs gap-1 cursor-help ${payrollConfig.configured === false ? 'text-amber-600' : ''}`}>
                        <Server className="w-3 h-3" />
                        {getProviderName()}
                        {payrollConfig.is_manual && <span className="text-slate-500">· mode fichier</span>}
                        {payrollConfig.configured === false && <span>· configuration incomplète</span>}
                        <Info className="w-3 h-3 opacity-70" />
                      </Badge>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-sm p-3">
                    <p className="font-medium text-xs mb-2">Ce que {getProviderName()} prend en charge</p>
                    <ul className="space-y-1">
                      {Object.entries(allCapabilityLabels).map(([cap, label]) => (
                        <li key={cap} className="flex items-center gap-2 text-xs">
                          {hasCapability(cap)
                            ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                            : <XCircle className="w-3 h-3 text-slate-400 shrink-0" />}
                          <span className={hasCapability(cap) ? '' : 'text-slate-400'}>{capabilityLabels[cap] || label}</span>
                        </li>
                      ))}
                    </ul>
                    {payrollConfig.units && (
                      <p className="text-xs mt-2 text-slate-400">Unités acceptées : {payrollConfig.units.join(', ')}</p>
                    )}
                    {payrollConfig.notes && <p className="text-xs mt-1 text-slate-400 italic">{payrollConfig.notes}</p>}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Badge variant="outline" className="text-xs text-amber-600">
                Aucun logiciel configuré
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Boutons pilotés par les capacités du logiciel de paie */}
          {canPushToProvider && (
            <Button
              variant="outline"
              className="gap-2 border-[#0F5156] text-[#0F5156]"
              onClick={handleSyncToPayroll}
              disabled={syncingPayroll || loadingPreview}
              title={payrollConfig?.configured === false ? `Configuration de ${getProviderName()} incomplète` : `Envoyer vers ${getProviderName()}`}
              data-testid="push-payroll-btn"
            >
              <Upload className="w-4 h-4" />
              {syncingPayroll ? 'Envoi...' : loadingPreview ? 'Aperçu...' : `Envoyer vers ${getProviderName()}`}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={`gap-2 ${exportFileIsPrimary ? 'border-[#0F5156] text-[#0F5156]' : ''}`}
                disabled={exportingFile}
                title={payrollConfig?.payroll_provider
                  ? `Fichier des éléments variables à importer dans ${getProviderName()}`
                  : 'Fichier des éléments variables (CSV / XLSX) à importer dans votre logiciel de paie'}
                data-testid="export-file-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                {exportingFile ? 'Génération...' : 'Exporter le fichier paie'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExportFile('csv')}>Fichier CSV (séparateur « ; »)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportFile('xlsx')}>Classeur Excel (XLSX)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setActiveTab('bulletins')}
            title={hasCapability('payslips.amounts')
              ? `Importer les bulletins depuis ${getProviderName()} ou déposer des PDF`
              : 'Déposez les bulletins PDF manuellement'}
          >
            {hasCapability('payslips.amounts') ? <Download className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
            {hasCapability('payslips.amounts') ? 'Bulletins' : 'Bulletins (dépôt PDF)'}
          </Button>

          {/* Calculator Sidebar */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="open-calculator-btn">
                <Calculator className="w-4 h-4" />
                Calculateur
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Calculateur d'heures</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-6">
                <div className="space-y-2">
                  <Label>Employe</Label>
                  <Select value={calcEmployee} onValueChange={setCalcEmployee}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.prenom} {emp.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type d'heures</Label>
                  <Select value={calcType} onValueChange={setCalcType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Heures normales (×1)</SelectItem>
                      <SelectItem value="sup_25">Heures sup 25% (×1.25)</SelectItem>
                      <SelectItem value="sup_50">Heures sup 50% (×1.50)</SelectItem>
                      <SelectItem value="nuit">Heures de nuit (×1.10)</SelectItem>
                      <SelectItem value="dimanche">Heures dimanche (×1.50)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nombre d'heures</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={calcHours}
                    onChange={(e) => setCalcHours(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <Button onClick={handleCalculate} className="w-full bg-[#0F5156] hover:bg-[#0d464a]">
                  Calculer
                </Button>
                
                {calcResult && (
                  <Card className="mt-4 bg-emerald-50 dark:bg-emerald-900/20">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">{calcResult.employee_name}</p>
                      <p className="text-xs text-slate-400">Taux horaire: {formatCurrency(calcResult.taux_horaire)}</p>
                      <div className="mt-2 p-3 bg-white dark:bg-slate-800 rounded-lg">
                        <p className="text-2xl font-bold text-emerald-600">
                          {formatCurrency(calcResult.montant)}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {calcResult.nb_heures}h × {formatCurrency(calcResult.taux_horaire)} × {calcResult.multiplicateur}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 w-full"
                        onClick={() => {
                          navigator.clipboard.writeText(calcResult.montant.toString());
                          toast.success('Montant copié');
                        }}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copier le montant
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </SheetContent>
          </Sheet>

          <Button variant="outline" onClick={handleExport} className="gap-2" data-testid="export-paie-btn">
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={handleExportPDF} className="gap-2">
            <FileDown className="w-4 h-4" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => changeMonth(-1)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <h2 className="text-xl font-semibold">{getMonthLabel(selectedMonth)}</h2>
              {customPeriod && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {new Date(customPeriod.date_debut + 'T00:00:00').toLocaleDateString('fr-FR')} → {new Date(customPeriod.date_fin + 'T00:00:00').toLocaleDateString('fr-FR')}
                </p>
              )}
              {isLocked && (
                <Badge className="mt-1 bg-red-100 text-red-800">
                  <Lock className="w-3 h-3 mr-1" />
                  Verrouillé
                </Badge>
              )}
            </div>
            <Button variant="ghost" onClick={() => changeMonth(1)}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Onglets Préparation / Bulletins */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="preparation" data-testid="tab-preparation">Préparation</TabsTrigger>
          <TabsTrigger value="bulletins" data-testid="tab-bulletins">Bulletins de paie</TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'bulletins' && (
        <BulletinsTab
          periode={selectedMonth}
          employees={employees}
          payrollConfig={payrollConfig}
          capabilities={payrollCapabilities}
          providerName={getProviderName()}
        />
      )}

      {activeTab === 'preparation' && (<>
      {/* Lock Banner */}
      {isLocked && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-red-600" />
              <div className="flex-1">
                <p className="font-medium text-red-800 dark:text-red-200">Période verrouillée</p>
                <p className="text-sm text-red-600 dark:text-red-300">
                  {lockMessage || `La date limite de modification (${dateLimite}) est dépassée. Les modifications ne sont plus possibles pour cette période.`}
                </p>
              </div>
              <Button variant="outline" onClick={handleExportPDF} className="gap-2 border-red-300 text-red-700 hover:bg-red-100">
                <Archive className="w-4 h-4" />
                Archiver (PDF)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Heures sup</p>
                <p className="text-2xl font-bold text-amber-500">{totals.heures_sup}h</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Heures nuit</p>
                <p className="text-2xl font-bold text-indigo-500">{totals.heures_nuit}h</p>
              </div>
              <Moon className="w-8 h-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total primes</p>
                <p className="text-2xl font-bold text-emerald-500">{formatCurrency(totals.primes)}</p>
              </div>
              <Euro className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Déductions</p>
                <p className="text-2xl font-bold text-red-500">-{formatCurrency(totals.deductions)}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Dialog open={isPrimeDialogOpen} onOpenChange={setIsPrimeDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              data-testid="add-prime-btn"
              disabled={isLocked}
              title={isLocked ? 'Période verrouillée' : ''}
            >
              {isLocked ? <Lock className="w-4 h-4 mr-2" /> : <Euro className="w-4 h-4 mr-2" />}
              Ajouter prime
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Ajouter une prime ou déduction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddPrime} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employé *</Label>
                <Select 
                  value={primeData.employee_id} 
                  onValueChange={(value) => setPrimeData({ ...primeData, employee_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.prenom} {emp.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={primeData.type_element} 
                  onValueChange={(value) => setPrimeData({ ...primeData, type_element: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prime">Prime</SelectItem>
                    <SelectItem value="deduction">Déduction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Rubrique</Label>
                <Select 
                  value={primeData.code_rubrique} 
                  onValueChange={(value) => {
                    const type = primeTypes.find(t => t.code === value);
                    setPrimeData({ 
                      ...primeData, 
                      code_rubrique: value,
                      libelle: type?.label || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {primeTypes.map((type) => (
                      <SelectItem key={type.code} value={type.code}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantité</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={primeData.quantite}
                    onChange={(e) => setPrimeData({ ...primeData, quantite: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Montant (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={primeData.montant}
                    onChange={(e) => setPrimeData({ ...primeData, montant: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsPrimeDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]">
                  Ajouter
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Acompte */}
        <Dialog open={isAcompteDialogOpen} onOpenChange={setIsAcompteDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="gap-2"
              disabled={isLocked}
              title={isLocked ? 'Période verrouillée' : ''}
            >
              {isLocked ? <Lock className="w-4 h-4" /> : <Wallet className="w-4 h-4" />}
              Saisir acompte
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Saisir un acompte</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddAcompte} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employé *</Label>
                <Select 
                  value={acompteData.employee_id} 
                  onValueChange={(value) => setAcompteData({ ...acompteData, employee_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.prenom} {emp.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={acompteData.montant}
                    onChange={(e) => setAcompteData({ ...acompteData, montant: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date versement *</Label>
                  <Input
                    type="date"
                    value={acompteData.date_versement}
                    onChange={(e) => setAcompteData({ ...acompteData, date_versement: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Motif</Label>
                <Input
                  value={acompteData.motif}
                  onChange={(e) => setAcompteData({ ...acompteData, motif: e.target.value })}
                  placeholder="Acompte sur salaire"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsAcompteDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]">
                  Enregistrer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bouton + Dialog Saisie sur salaire (création / modification) */}
        <Button
          variant="outline"
          className="gap-2"
          disabled={isLocked}
          title={isLocked ? 'Période verrouillée' : 'Créer une saisie sur salaire'}
          onClick={() => openCreateSaisie()}
          data-testid="add-saisie-btn"
        >
          {isLocked ? <Lock className="w-4 h-4" /> : <Gavel className="w-4 h-4" />}
          Saisie sur salaire
        </Button>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => openSaisiesList('all')}
          title="Saisies sur salaire en cours"
          data-testid="list-saisies-btn"
        >
          <Scale className="w-4 h-4" />
          Saisies en cours
          {saisies.filter(s => s.statut === 'active').length > 0 && (
            <Badge className="bg-rose-100 text-rose-800 ml-1">{saisies.filter(s => s.statut === 'active').length}</Badge>
          )}
        </Button>
        <Dialog open={isSaisieDialogOpen} onOpenChange={(open) => { setIsSaisieDialogOpen(open); if (!open) setEditingSaisieId(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingSaisieId ? 'Modifier la saisie sur salaire' : 'Nouvelle saisie sur salaire'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveSaisie} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employé *</Label>
                <Select
                  value={saisieData.employee_id}
                  onValueChange={(value) => setSaisieData({ ...saisieData, employee_id: value })}
                  disabled={Boolean(editingSaisieId)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.prenom} {emp.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Créancier *</Label>
                  <Input
                    value={saisieData.creancier}
                    onChange={(e) => setSaisieData({ ...saisieData, creancier: e.target.value })}
                    placeholder="Trésor Public, huissier..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Référence</Label>
                  <Input
                    value={saisieData.reference}
                    onChange={(e) => setSaisieData({ ...saisieData, reference: e.target.value })}
                    placeholder="N° de dossier"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant total (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={saisieData.montant_total}
                    onChange={(e) => setSaisieData({ ...saisieData, montant_total: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mode de retenue</Label>
                  <Select
                    value={saisieData.mode}
                    onValueChange={(value) => setSaisieData({ ...saisieData, mode: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensualite">Mensualité fixe</SelectItem>
                      <SelectItem value="quotite">Quotité (% du salaire de base)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {saisieData.mode === 'quotite' ? (
                  <div className="space-y-2">
                    <Label>Quotité (%) *</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={saisieData.quotite_pct}
                      onChange={(e) => setSaisieData({ ...saisieData, quotite_pct: e.target.value })}
                      required
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Mensualité (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={saisieData.mensualite}
                      onChange={(e) => setSaisieData({ ...saisieData, mensualite: e.target.value })}
                      required
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Date de début *</Label>
                  <Input
                    type="date"
                    value={saisieData.date_debut}
                    onChange={(e) => setSaisieData({ ...saisieData, date_debut: e.target.value })}
                    disabled={Boolean(editingSaisieId)}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de fin</Label>
                  <Input
                    type="date"
                    value={saisieData.date_fin}
                    onChange={(e) => setSaisieData({ ...saisieData, date_fin: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Commentaire</Label>
                  <Input
                    value={saisieData.commentaire}
                    onChange={(e) => setSaisieData({ ...saisieData, commentaire: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsSaisieDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]" disabled={!saisieData.employee_id}>
                  Enregistrer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog liste des saisies en cours */}
        <Dialog open={isSaisiesListOpen} onOpenChange={setIsSaisiesListOpen}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5" />
                Saisies sur salaire - {getMonthLabel(selectedMonth)}
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-between gap-2 py-2">
              <Select value={saisiesListEmployee} onValueChange={setSaisiesListEmployee}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Tous les employés" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les employés</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                disabled={isLocked}
                onClick={() => openCreateSaisie(saisiesListEmployee === 'all' ? '' : saisiesListEmployee)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Nouvelle saisie
              </Button>
            </div>
            {(() => {
              const list = saisies.filter(s => saisiesListEmployee === 'all' || s.employee_id === saisiesListEmployee);
              if (list.length === 0) {
                return <p className="text-sm text-slate-500 py-6 text-center">Aucune saisie sur salaire</p>;
              }
              return (
                <div className="overflow-x-auto">
                  <Table className="text-sm">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employé</TableHead>
                        <TableHead>Créancier</TableHead>
                        <TableHead className="text-right">Montant total</TableHead>
                        <TableHead className="text-right">Retenu</TableHead>
                        <TableHead className="text-right">Solde restant</TableHead>
                        <TableHead className="text-right">Retenue du mois</TableHead>
                        <TableHead className="text-center">Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {list.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium">{s.employee_name || '-'}</TableCell>
                          <TableCell>
                            <p>{s.creancier}</p>
                            <p className="text-xs text-slate-500">
                              {s.reference ? `Réf. ${s.reference} - ` : ''}
                              {s.mode === 'quotite' ? `${s.quotite_pct}% du salaire` : `${formatCurrency(s.mensualite || 0)}/mois`}
                              {s.date_debut ? ` - depuis le ${new Date(s.date_debut).toLocaleDateString('fr-FR')}` : ''}
                            </p>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(s.montant_total || 0)}</TableCell>
                          <TableCell className="text-right text-slate-500">{formatCurrency(s.montant_retenu_cumule || 0)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(s.solde_restant || 0)}</TableCell>
                          <TableCell className="text-right">
                            {s.retenue_periode > 0 ? (
                              <span className="font-semibold text-rose-600">-{formatCurrency(s.retenue_periode)}</span>
                            ) : <span className="text-slate-400">-</span>}
                          </TableCell>
                          <TableCell className="text-center">{getSaisieStatusBadge(s.statut)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Modifier" disabled={isLocked} onClick={() => openEditSaisie(s)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              {s.statut !== 'soldee' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs"
                                  title={s.statut === 'suspendue' ? 'Réactiver' : 'Suspendre'}
                                  disabled={isLocked}
                                  onClick={() => handleToggleSuspendSaisie(s)}
                                >
                                  {s.statut === 'suspendue' ? 'Réactiver' : 'Suspendre'}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-red-500"
                                title={(s.retenues || []).length > 0 ? 'Suppression impossible : des retenues sont enregistrées' : 'Supprimer'}
                                disabled={isLocked || (s.retenues || []).length > 0}
                                onClick={() => handleDeleteSaisie(s.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Dialog Jour Férié Travaillé */}
        <Dialog open={isJourFerieDialogOpen} onOpenChange={setIsJourFerieDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="gap-2"
              disabled={isLocked}
              title={isLocked ? 'Période verrouillée' : ''}
            >
              {isLocked ? <Lock className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
              Jour férié travaillé
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Déclarer un jour férié travaillé</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddJourFerieTravaille} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employé *</Label>
                <Select 
                  value={jourFerieData.employee_id} 
                  onValueChange={(value) => setJourFerieData({ ...jourFerieData, employee_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.prenom} {emp.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date du jour férié *</Label>
                  <Input
                    type="date"
                    value={jourFerieData.date}
                    onChange={(e) => setJourFerieData({ ...jourFerieData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heures travaillées *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={jourFerieData.heures_travaillees}
                    onChange={(e) => setJourFerieData({ ...jourFerieData, heures_travaillees: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Taux de majoration (%) - laisser vide pour utiliser le taux par défaut</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  placeholder="25"
                  value={jourFerieData.taux_majoration || ''}
                  onChange={(e) => setJourFerieData({ ...jourFerieData, taux_majoration: e.target.value ? parseFloat(e.target.value) : null })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsJourFerieDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]">
                  Enregistrer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Modifier Élément */}
        <Dialog open={isEditElementDialogOpen} onOpenChange={setIsEditElementDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier l'élément</DialogTitle>
            </DialogHeader>
            {editElementData && (
              <form onSubmit={handleUpdateElement} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Libellé</Label>
                  <Input
                    value={editElementData.libelle || ''}
                    onChange={(e) => setEditElementData({ ...editElementData, libelle: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Quantité</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={editElementData.quantite || 0}
                      onChange={(e) => setEditElementData({ ...editElementData, quantite: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Montant (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editElementData.montant || 0}
                      onChange={(e) => setEditElementData({ ...editElementData, montant: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsEditElementDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]">
                    Sauvegarder
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par employé..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="brouillon">Brouillon</SelectItem>
                <SelectItem value="valide">Validé</SelectItem>
                <SelectItem value="exporte">Exporté</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Recap Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="w-full text-sm">
              <TableHeader>
                <TableRow>
                  <SortHeader field="nom" label="Employé" />
                  <SortHeader field="matricule" label="Mat." className="text-center" />
                  <TableHead className="table-header text-left text-xs">Absences</TableHead>
                  <SortHeader field="heures_sup_25" label="HS 25%" className="text-center" />
                  <SortHeader field="heures_sup_50" label="HS 50%" className="text-center" />
                  <SortHeader field="heures_nuit" label="H. Nuit" className="text-center" />
                  <SortHeader field="total_primes" label="Primes" className="text-right" />
                  <TableHead className="table-header text-right">Paniers</TableHead>
                  <TableHead className="table-header text-right">Acomptes</TableHead>
                  <TableHead className="table-header text-right">Saisies</TableHead>
                  <TableHead className="table-header text-right">Salaire Brut</TableHead>
                  <TableHead className="table-header">Notes</TableHead>
                  <TableHead className="table-header text-center">Statut</TableHead>
                  <TableHead className="table-header text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecaps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-8 text-slate-500">
                      Aucune donnée pour ce mois
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecaps.map((recap) => {
                    const paniers = recap.paniers_repas;
                    const paniersTotal = paniers ? paniers.montant_total : 0;
                    const paniersJours = paniers ? paniers.nb_jours : 0;
                    const hasAbsences = recap.absences && recap.absences.length > 0;
                    const rowValidated = recap.statut === 'valide';
                    const rowLocked = isLocked || rowValidated;

                    return (
                    <TableRow
                      key={recap.employee_id}
                      className="table-row cursor-pointer"
                      onClick={(e) => { if (e.target.closest('button, input, select, a, [role="combobox"]')) return; openEmployeeSheet(recap.employee_id); }}
                      title="Cliquer pour ouvrir la fiche paie du salarié"
                      data-testid={`recap-row-${recap.employee_id}`}
                    >
                      <TableCell className="table-cell">
                        <span className="font-medium">{recap._nom}</span> <span className="text-slate-500">{recap._prenom}</span>
                      </TableCell>
                      <TableCell className="table-cell text-center font-mono text-xs">{recap._matricule || '-'}</TableCell>
                      <TableCell className="table-cell">
                        {hasAbsences ? (
                          <div className="flex flex-col gap-0.5">
                            {recap.absences.map((abs, ai) => (
                              <span key={ai} className="inline-flex items-center text-[10px] leading-tight">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${
                                  abs.type === 'cp' ? 'bg-blue-500' :
                                  abs.type === 'rtt' ? 'bg-purple-500' :
                                  abs.type === 'maladie' ? 'bg-red-500' :
                                  abs.type === 'sans_solde' ? 'bg-slate-500' :
                                  abs.type === 'formation' ? 'bg-cyan-500' :
                                  'bg-amber-500'
                                }`}></span>
                                <span className="text-slate-600 dark:text-slate-400">{abs.type_label} {abs.nb_jours}j</span>
                              </span>
                            ))}
                          </div>
                        ) : <span className="text-slate-300 text-xs">-</span>}
                      </TableCell>
                      <EditableCell employeeId={recap.employee_id} field="heures_sup_25" value={recap.heures_sup_25} suffix="h" className="text-center" />
                      <EditableCell employeeId={recap.employee_id} field="heures_sup_50" value={recap.heures_sup_50} suffix="h" className="text-center" />
                      <EditableCell employeeId={recap.employee_id} field="heures_nuit" value={recap.heures_nuit} suffix="h" className="text-center" />
                      <TableCell className="table-cell text-right">
                        {recap.primes && recap.primes.length > 0 ? (
                          <div className="text-[10px] leading-relaxed">
                            {recap.primes.map((p, pi) => (
                              <div key={pi} className="flex justify-between gap-2">
                                <span className="text-slate-500 truncate max-w-[80px]" title={p.libelle || p.code_rubrique}>{p.libelle || p.code_rubrique}</span>
                                <span className="text-emerald-600 whitespace-nowrap">{formatCurrency(p.montant)}</span>
                              </div>
                            ))}
                            {recap.primes.length > 1 && (
                              <div className="flex justify-between gap-2 border-t border-slate-200 dark:border-slate-600 pt-0.5 mt-0.5 font-semibold text-xs">
                                <span>Total</span><span className="text-emerald-600">{formatCurrency(recap.total_primes)}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        {(() => {
                          const isPaniersEditing = editingCell?.employeeId === recap.employee_id && editingCell?.field === 'paniers';
                          if (isPaniersEditing) {
                            return (
                              <Input
                                type="number"
                                step="1"
                                min="0"
                                className="h-7 w-16 text-sm text-center"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={() => handleInlinePaniers(recap.employee_id, editingValue)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleInlinePaniers(recap.employee_id, editingValue);
                                  if (e.key === 'Escape') setEditingCell(null);
                                }}
                                autoFocus
                              />
                            );
                          }
                          return (
                            <div
                              className={`text-xs ${!rowLocked ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-1 py-0.5' : ''}`}
                              onDoubleClick={() => {
                                if (!rowLocked) {
                                  setEditingCell({ employeeId: recap.employee_id, field: 'paniers' });
                                  setEditingValue(String(paniersJours || 0));
                                }
                              }}
                              title={!rowLocked ? 'Double-cliquez pour modifier les jours paniers' : (rowValidated ? 'Paie validée : dévalidez pour modifier' : '')}
                            >
                              {paniers ? (
                                <>
                                  <span className="font-semibold text-blue-600">{formatCurrency(paniersTotal)}</span>
                                  <p className="text-slate-400">{paniersJours}j</p>
                                </>
                              ) : (
                                <span className="text-slate-400">0j</span>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        {recap.total_acomptes > 0 ? (
                          <span className="font-semibold text-purple-600">
                            -{formatCurrency(recap.total_acomptes)}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      {/* Saisies sur salaire */}
                      <TableCell className="table-cell text-right">
                        {recap.total_saisies > 0 ? (
                          <span
                            className="font-semibold text-rose-600 cursor-pointer"
                            title={(recap.saisies || []).map(s => `${s.creancier} : ${formatCurrency(s.montant)}`).join(' | ')}
                            onClick={() => openSaisiesList(recap.employee_id)}
                          >
                            -{formatCurrency(recap.total_saisies)}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      {/* Salaire Brut détaillé */}
                      <TableCell className="table-cell text-right">
                        {(() => {
                          const tauxHoraire = recap.taux_horaire || 0;
                          const dureeHebdo = recap.duree_hebdo || 35;
                          const brutMensuel = recap.salaire_base || 0;
                          const brutHS = (recap.heures_sup_25 * tauxHoraire * 1.25) + (recap.heures_sup_50 * tauxHoraire * 1.5) + (recap.heures_nuit * tauxHoraire * 1.1);
                          const primesTotal = recap.total_primes || 0;
                          const paniersM = paniers ? paniers.montant_total : 0;
                          const totalBrut = brutMensuel + brutHS + primesTotal + paniersM;
                          return (
                            <div className="text-[10px] leading-relaxed whitespace-nowrap">
                              <div className="flex justify-between gap-2"><span className="text-slate-500">Base</span><span>{formatCurrency(brutMensuel)}</span></div>
                              {brutHS > 0 && <div className="flex justify-between gap-2"><span className="text-amber-600">HS</span><span className="text-amber-600">{formatCurrency(Math.round(brutHS * 100) / 100)}</span></div>}
                              {primesTotal > 0 && <div className="flex justify-between gap-2"><span className="text-emerald-600">Primes</span><span className="text-emerald-600">{formatCurrency(primesTotal)}</span></div>}
                              {paniersM > 0 && <div className="flex justify-between gap-2"><span className="text-blue-600">Paniers</span><span className="text-blue-600">{formatCurrency(paniersM)}</span></div>}
                              <div className="flex justify-between gap-2 border-t border-slate-200 dark:border-slate-600 pt-0.5 mt-0.5 font-semibold text-xs"><span>Total</span><span>{formatCurrency(Math.round(totalBrut * 100) / 100)}</span></div>
                            </div>
                          );
                        })()}
                      </TableCell>
                      {/* Notes éditables */}
                      <TableCell className="table-cell min-w-[120px]">
                        {(() => {
                          const isNotesEditing = editingCell?.employeeId === recap.employee_id && editingCell?.field === 'notes';
                          const currentNotes = recap.statut_doc?.commentaire || recap.commentaire || '';
                          if (isNotesEditing) {
                            return (
                              <textarea
                                className="w-full text-xs border rounded px-2 py-1 bg-white dark:bg-slate-800 dark:border-slate-600 resize-none"
                                rows={2}
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={() => handleInlineNotes(recap.employee_id, editingValue)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') setEditingCell(null);
                                }}
                                autoFocus
                              />
                            );
                          }
                          return (
                            <div
                              className={`text-xs text-slate-500 ${!rowLocked ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-1 py-0.5 min-h-[24px]' : 'min-h-[24px]'}`}
                              onDoubleClick={() => {
                                if (!rowLocked) {
                                  setEditingCell({ employeeId: recap.employee_id, field: 'notes' });
                                  setEditingValue(currentNotes);
                                }
                              }}
                              title={!rowLocked ? 'Double-cliquez pour ajouter une note' : (rowValidated ? 'Paie validée : dévalidez pour modifier' : '')}
                            >
                              {currentNotes || <span className="text-slate-300 italic">-</span>}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="table-cell">
                        <div className="flex items-center gap-1">
                          {getStatusBadge(recap.statut)}
                          {rowValidated && (
                            <Lock className="w-3 h-3 text-emerald-700" title="Ligne verrouillée : dévalidez pour modifier" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEmployeeSheet(recap.employee_id)}
                            title={rowLocked ? 'Consulter la fiche paie (lecture seule)' : 'Ouvrir la fiche paie du salarié'}
                            data-testid={`edit-recap-${recap.employee_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {recap.statut === 'brouillon' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-600 hover:bg-emerald-50"
                              onClick={() => handleValidate(recap.employee_id)}
                              title="Valider et archiver"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {recap.statut === 'valide' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-amber-600 hover:bg-amber-50"
                              onClick={() => handleInvalidate(recap.employee_id)}
                              title="Invalider la paie"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )})
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Workflow Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Workflow d'export paie
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-slate-300 text-slate-700 flex items-center justify-center text-sm font-bold">1</div>
                <span className="font-medium">Saisie</span>
              </div>
              <p className="text-sm text-slate-500">Saisir les heures sup, primes et déductions</p>
            </div>
            <div className="flex-1 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold">2</div>
                <span className="font-medium">Validation</span>
              </div>
              <p className="text-sm text-slate-500">Vérifier et valider les données</p>
            </div>
            <div className="flex-1 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">3</div>
                <span className="font-medium">Export</span>
              </div>
              <p className="text-sm text-slate-500">Export CSV compatible SILAE, CEGID, PayFit, ADP...</p>
            </div>
          </div>
        </CardContent>
      </Card>
      </>)}

      {/* Fiche paie complète d'un salarié (clic sur la ligne) */}
      <EmployeePayrollSheet
        open={isEditRecapOpen}
        onOpenChange={(open) => { setIsEditRecapOpen(open); if (!open) setEditRecapEmployeeId(null); }}
        employeeId={editRecapEmployeeId}
        periode={selectedMonth}
        isLocked={isLocked}
        canEdit={hasPermission('payroll_edit')}
        primeTypes={primeTypes}
        hasPrev={sheetIndex > 0}
        hasNext={sheetIndex >= 0 && sheetIndex < filteredRecaps.length - 1}
        onNavigate={(direction) => {
          const next = filteredRecaps[sheetIndex + direction];
          if (next) setEditRecapEmployeeId(next.employee_id);
        }}
        onSaved={(recap) => {
          if (recap) setRecaps((prev) => prev.map((r) => (r.employee_id === recap.employee_id ? { ...r, ...recap } : r)));
          else fetchData();
        }}
        onOpenSaisies={(employeeId) => openSaisiesList(employeeId)}
      />

      {/* Dialog aperçu de l'envoi (période complète) + alerte doublon */}
      <Dialog open={showSyncConfirm} onOpenChange={(open) => { setShowSyncConfirm(open); if (!open) setSyncPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#0F5156]" />
              Envoyer vers {syncPreview?.provider || getProviderName() || 'le logiciel de paie'} — {getMonthLabel(selectedMonth)}
            </DialogTitle>
          </DialogHeader>
          {loadingPreview || !syncPreview ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0F5156]"></div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="push-preview">
              {/* Compteurs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-center">
                  <p className="text-xl font-bold">{syncPreview.employees_count}</p>
                  <p className="text-xs text-slate-500">salarié(s) avec matricule</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-center">
                  <p className="text-xl font-bold text-[#0F5156]">{syncPreview.elements_count}</p>
                  <p className="text-xs text-slate-500">élément(s) à envoyer</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${syncPreview.non_mappes?.length ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-slate-50 dark:bg-slate-800'}`}>
                  <p className={`text-xl font-bold ${syncPreview.non_mappes?.length ? 'text-amber-700' : ''}`}>{syncPreview.non_mappes?.length || 0}</p>
                  <p className="text-xs text-slate-500">rubrique(s) non mappée(s)</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${syncPreview.elements_exclus?.length ? 'bg-red-50 dark:bg-red-900/20' : 'bg-slate-50 dark:bg-slate-800'}`}>
                  <p className={`text-xl font-bold ${syncPreview.elements_exclus?.length ? 'text-red-700' : ''}`}>{syncPreview.elements_exclus?.length || 0}</p>
                  <p className="text-xs text-slate-500">exclu(s) : unité</p>
                </div>
              </div>

              {syncPreview.message && !syncPreview.peut_envoyer && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                  {syncPreview.message}
                </div>
              )}

              {/* Doublon */}
              {syncWarningData?.already_synced && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200 font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Données déjà envoyées pour cette période
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    Dernier envoi le {syncWarningData.last_sync_date ? new Date(syncWarningData.last_sync_date).toLocaleString('fr-FR') : '-'} :
                    {' '}{syncWarningData.last_sync_elements || 0} élément(s) pour {syncWarningData.last_sync_employees || 0} salarié(s).
                    Un nouvel envoi peut créer des doublons : supprimez les éléments précédents dans {syncPreview.provider || 'le logiciel'} avant de renvoyer.
                  </p>
                </div>
              )}

              {/* Rubriques non mappées */}
              {syncPreview.non_mappes?.length > 0 && (
                <div className="border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Rubriques sans code {syncPreview.provider} (non envoyées)</p>
                    <Link to="/admin?section=payroll-export" className="text-xs text-[#0F5156] underline inline-flex items-center gap-1" onClick={() => setShowSyncConfirm(false)}>
                      Administration › Codes paie <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    <Table>
                      <TableBody>
                        {syncPreview.non_mappes.slice(0, 50).map((nm, i) => (
                          <TableRow key={`nm-${i}`}>
                            <TableCell className="py-1.5 text-xs">{nm.employee_name || nm.matricule}</TableCell>
                            <TableCell className="py-1.5 text-xs font-mono">{nm.nexus_code}</TableCell>
                            <TableCell className="py-1.5 text-xs">{nm.libelle}</TableCell>
                            <TableCell className="py-1.5 text-xs text-right">{nm.valeur} {nm.unite}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Éléments exclus (unité non prise en charge) */}
              {syncPreview.elements_exclus?.length > 0 && (
                <div className="border border-red-200 dark:border-red-800 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">Éléments exclus : unité non prise en charge par {syncPreview.provider}</p>
                    <p className="text-xs text-red-700 dark:text-red-300">Unités acceptées : {(syncPreview.units || []).join(', ')}. Saisissez-les directement dans le logiciel de paie ou utilisez l'export fichier.</p>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    <Table>
                      <TableBody>
                        {syncPreview.elements_exclus.slice(0, 50).map((x, i) => (
                          <TableRow key={`ex-${i}`}>
                            <TableCell className="py-1.5 text-xs">{x.employee_name || x.matricule}</TableCell>
                            <TableCell className="py-1.5 text-xs font-mono">{x.code}</TableCell>
                            <TableCell className="py-1.5 text-xs">{x.libelle}</TableCell>
                            <TableCell className="py-1.5 text-xs text-right">{x.valeur} {x.unite}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Conversions et autres avertissements */}
              {(syncPreview.conversions?.length > 0 || syncPreview.employees_sans_matricule?.length > 0) && (
                <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1 list-disc pl-5">
                  {syncPreview.conversions?.length > 0 && (
                    <li>{syncPreview.conversions.length} absence(s) en jours convertie(s) en heures (durée hebdomadaire / 5) : {syncPreview.provider} n'accepte pas l'unité JOURS.</li>
                  )}
                  {syncPreview.employees_sans_matricule?.length > 0 && (
                    <li>{syncPreview.employees_sans_matricule.length} salarié(s) actif(s) sans matricule paie ignoré(s) : {syncPreview.employees_sans_matricule.slice(0, 5).map(e => `${e.prenom || ''} ${e.nom || ''}`.trim()).join(', ')}{syncPreview.employees_sans_matricule.length > 5 ? '…' : ''}</li>
                  )}
                </ul>
              )}

              {/* Détail des éléments envoyés */}
              {syncPreview.elements?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600 dark:text-slate-300">Voir les {syncPreview.elements.length} élément(s) tels qu'ils seront transmis</summary>
                  <div className="max-h-56 overflow-y-auto mt-2 border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Matricule</TableHead>
                          <TableHead className="text-xs">Code</TableHead>
                          <TableHead className="text-xs">Libellé</TableHead>
                          <TableHead className="text-xs text-right">Valeur</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {syncPreview.elements.map((e, i) => (
                          <TableRow key={`el-${i}`}>
                            <TableCell className="py-1 text-xs font-mono">{e.matricule}</TableCell>
                            <TableCell className="py-1 text-xs font-mono">{e.code}</TableCell>
                            <TableCell className="py-1 text-xs">{e.libelle}</TableCell>
                            <TableCell className="py-1 text-xs text-right">{e.valeur} {e.unite}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </details>
              )}

              <div className="flex justify-between items-center gap-3 pt-2">
                <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => { setShowSyncConfirm(false); handleExportFile('csv'); }}>
                  <FileSpreadsheet className="w-3 h-3" /> Exporter plutôt le fichier
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowSyncConfirm(false)}>Annuler</Button>
                  <Button
                    className={syncWarningData?.already_synced ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-[#0F5156] hover:bg-[#0d464a] text-white'}
                    onClick={executeSyncToPayroll}
                    disabled={!syncPreview.peut_envoyer || syncPreview.elements_count === 0}
                    data-testid="confirm-push-btn"
                  >
                    {syncWarningData?.already_synced ? <AlertTriangle className="w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    {syncWarningData?.already_synced ? 'Envoyer quand même' : `Envoyer ${syncPreview.elements_count} élément(s)`}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payroll;
